import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import {
  AngleMode,
  BazisMaterialHelper,
  Chamfer,
  Countertop,
  CountertopAngleProcessing,
  CountertopAngleProcessingAvailability,
  CountertopLine,
  CountertopOutline,
  Cut,
  CutInside,
  CuttingButt,
  CuttingPanelHelper,
  MasonsMitreOrientationEnum,
  OutlineEditorOptions,
  Rounding,
  RoundingInside,
  SimpleCountertop,
} from 'cloud-shared-lib';

import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import _ from 'lodash';
import { Observable, lastValueFrom, tap } from 'rxjs';
import { AngleModeSelectComponent } from './angle-mode-select/angle-mode-select.component';
import { AngleSettingsService } from './angle-settings.service';
import { ButtSelectComponent } from './butt-select/butt-select.component';
import { ChamferInputComponent } from './chamfer-input/chamfer-input.component';
import { CutInputComponent } from './cut-input/cut-input.component';
import { InsideCutInputComponent } from './inside-cut-input/inside-cut-input.component';
import { InsideCutWithAngleInputComponent } from './inside-cut-with-angle-input/inside-cut-with-angle-input.component';
import { MasonsMitreFInputComponent } from './masons-mitre-input/masons-mitre-input.component';
import { RoundingInputComponent } from './rounding-input/rounding-input.component';

export class AngleSettingsBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _AngleSettingsMixinBase = mixinDisabled(AngleSettingsBase);

@Component({
  selector: 'csl-countertop-angle-settings',
  templateUrl: './countertop-angle-settings.component.html',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    FlexModule,
    AngleModeSelectComponent,
    NgSwitch,
    NgSwitchCase,
    NgIf,
    CutInputComponent,
    RoundingInputComponent,
    InsideCutInputComponent,
    ChamferInputComponent,
    MasonsMitreFInputComponent,
    NgSwitchDefault,
    ButtSelectComponent,
    InsideCutWithAngleInputComponent,
    AsyncPipe,
  ],
  providers: [AngleSettingsService],
})
export class CountertopAngleSettingsComponent extends _AngleSettingsMixinBase implements CanDisable, OnChanges {
  @Input() angleProcessingAvailability: CountertopAngleProcessingAvailability;
  @Input() countertop: Countertop;
  @Input() angleNumber: number;
  @Input() outlineOptions: OutlineEditorOptions;
  @Input()
  get dense(): boolean {
    return this._dense;
  }
  set dense(value: BooleanInput) {
    this._dense = coerceBooleanProperty(value);
  }

  private _xMax = 0;
  private _yMax = 0;

  matchedEdgeBanding: CuttingButt[] = [];
  matchedEdgeBanding$: Observable<CuttingButt[]>;

  thisAngleMode = AngleMode;

  thisMasonsMitreOrientationEnum = MasonsMitreOrientationEnum;

  _dense = false;

  edgeBanding: CuttingButt[] = [];

  get outline(): CountertopOutline {
    return this.countertop.outline;
  }

  get disableButt(): boolean {
    return (
      this.angle.mode === AngleMode.None ||
      this.angle.mode === AngleMode.MasonsMitreMale ||
      this.angle.mode === AngleMode.MasonsMitreFemale ||
      (this.angle.mode === AngleMode.CuttedInside &&
        !this.outlineOptions.angles.insideCutting.restrictions.addButtToCutting)
    );
  }

  @Output() anglesChange: EventEmitter<CountertopOutline> = new EventEmitter();

  get angle(): CountertopAngleProcessing {
    const init = (): CountertopAngleProcessing => {
      const angle = new CountertopAngleProcessing();
      angle.mode = AngleMode.None;
      angle.angleNumber = this.angleNumber;

      return angle;
    };

    const _angle = this.outline.angleProcessing[this.angleNumber] ?? init();

    this.outline.angleProcessing[this.angleNumber] = _angle;

    return this.outline.angleProcessing[this.angleNumber];
  }

  get widthLine(): number {
    return this.countertopLine().width;
  }

  get lengthLine(): number {
    return this.countertopLine().length;
  }

  constructor(private angleSettingsService: AngleSettingsService) {
    super();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const angle = changes.angleNumber?.currentValue ?? this.angleNumber;
    const countertop = changes.countertop?.currentValue ?? this.countertop;

    if (!_.isNil(countertop) && !_.isNil(angle)) {
      const neighbors = countertop.getAngleNeighbors(angle.toString());

      this.matchedEdgeBanding$ = this.angleSettingsService
        .getMatchedButtsForClient(this.countertop.material.inMaterialBaseId, this.countertop.material.thickness)
        .pipe(tap(res => (this.matchedEdgeBanding = res)));

      await lastValueFrom(this.matchedEdgeBanding$);

      this.edgeBanding = this.getButtListForAngle(
        countertop.outline.edgeBanding[neighbors.leftSide],
        countertop.outline.edgeBanding[neighbors.rightSide],
        this.angle?.parameters?.edgeBanding,
        this.matchedEdgeBanding
      );

      this._xMax = this.getXMax();
      this._yMax = this.getYMax();
    }
  }

  getAllMin = (value: number): number => Math.min(...[value, this._yMax, this._xMax]);

  getXMin = (value: number): number => Math.min(...[value, this._xMax]);

  getYMin = (value: number): number => Math.min(...[value, this._yMax]);

  getMax(...values: number[]): number {
    return Math.max(...values);
  }

  getRMax(...values: number[]): number {
    return Math.min(...values);
  }

  setAngleParams(type: AngleMode): void {
    this.angle.reset(type);

    this.angle.parameters.edgeBanding = this.getDefaultEdgeBanding();

    this.anglesChange.emit(this.outline);
  }

  getDefaultEdgeBanding(): CuttingButt | undefined {
    const { leftSide, rightSide } = this.countertop.getAngleNeighbors(this.angleNumber.toString());

    const leftEdgeBanding = this.countertop.outline.edgeBanding[leftSide];
    const rightEdgeBanding = this.countertop.outline.edgeBanding[rightSide];

    const isSLeftSidePostforming = this.countertop.checkSidePostforming(leftSide);
    const isSRightSidePostforming = this.countertop.checkSidePostforming(rightSide);

    const hasOneSidePostforming =
      (isSLeftSidePostforming && !isSRightSidePostforming) || (!isSLeftSidePostforming && isSRightSidePostforming);

    if (hasOneSidePostforming || BazisMaterialHelper.compareMaterials(leftEdgeBanding, rightEdgeBanding)) {
      return leftEdgeBanding || rightEdgeBanding;
    }

    return undefined;
  }

  getButtListForAngle(
    firstSideButt?: CuttingButt,
    secondSideButt?: CuttingButt,
    buttOnAngle?: CuttingButt,
    matchedButts?: CuttingButt[]
  ): CuttingButt[] {
    const butts = [];

    const compareWithFirst = CuttingPanelHelper.compareButts(firstSideButt, buttOnAngle);
    const compareWithSecond = CuttingPanelHelper.compareButts(secondSideButt, buttOnAngle);
    const compareAdjacent = CuttingPanelHelper.compareButts(secondSideButt, firstSideButt);

    butts.push(firstSideButt && (compareWithFirst ? buttOnAngle : firstSideButt));
    butts.push(secondSideButt && !compareAdjacent && (compareWithSecond ? buttOnAngle : secondSideButt));
    butts.push(!compareWithFirst && !compareWithSecond && buttOnAngle);

    matchedButts.forEach(mb => {
      const hasNo = butts
        .map(b => {
          return CuttingPanelHelper.compareButts(mb, b);
        })
        .every(v => !v);

      if (hasNo) {
        butts.push(mb);
      }
    });

    return butts.filter(Boolean);
  }

  private countertopLine = (): CountertopLine => this.countertop.getLineByAngleNumber(this.angleNumber);

  private getYMax(): number {
    const getRestrictionsOnY = (originalY: number): number => {
      let y = originalY;
      const isSimpleCountertop = this.countertop instanceof SimpleCountertop;
      const neighborsByY = this.countertop.getNeighborsByAxis(this.angleNumber.toString(), 'y');
      const sideLength = this.countertop.getSideLengthByNumber(neighborsByY.side);
      let angleMaxValue = 0;

      if (!isSimpleCountertop) y = Math.min(sideLength, y);

      if (originalY >= sideLength) angleMaxValue = this.getAngleMax(neighborsByY.angle).y;

      y -= angleMaxValue;

      return y;
    };
    const countertopLine = this.countertopLine();

    return countertopLine.isVertical
      ? getRestrictionsOnY(countertopLine.length)
      : getRestrictionsOnY(countertopLine.width);
  }

  private getXMax(): number {
    const getRestrictionsOnX = (originalX: number): number => {
      let x = originalX;
      const isSimpleCountertop = this.countertop instanceof SimpleCountertop;
      const neighborsByX = this.countertop.getNeighborsByAxis(this.angleNumber.toString(), 'x');
      const sideLength = this.countertop.getSideLengthByNumber(neighborsByX.side);
      let angleMaxValue = 0;

      if (!isSimpleCountertop) x = Math.min(originalX, sideLength);

      if (originalX >= sideLength) angleMaxValue = this.getAngleMax(neighborsByX.angle).x;

      x -= angleMaxValue;

      return x;
    };

    return this.countertopLine().isVertical
      ? getRestrictionsOnX(this.countertopLine().width)
      : getRestrictionsOnX(this.countertopLine().length);
  }

  private getAngleMax(angleNumber: string): { x: number; y: number } {
    let xMax = 0;
    let yMax = 0;

    const angle: CountertopAngleProcessing = this.countertop.outline.angleProcessing[angleNumber];

    if (!angle || !angle.parameters) return { x: xMax, y: yMax };

    if (angle.parameters instanceof Cut || angle.parameters instanceof CutInside || angle.parameters instanceof Cut) {
      xMax = angle.parameters.x;
      yMax = angle.parameters.y;
    }

    if (angle.parameters instanceof Rounding || angle.parameters instanceof RoundingInside) {
      xMax = angle.parameters.r;
      yMax = angle.parameters.r;
    }

    if (angle.parameters instanceof Chamfer) {
      xMax = angle.parameters.length;
      yMax = angle.parameters.length;
    }

    return { x: xMax, y: yMax };
  }
}
